import { ReactNode } from "react";
import { DEFAULT_FIELD_NAMES, FieldNamesType, IS_CHILDREN, modeType, SelectOption, SelectValue } from "./interface";

/** 判断改选项是否被选中 */
export function isOptionSelect(value?: SelectValue, optionValue?: string) {
  if (value === '' || value === undefined || value === null) return false;
  if (optionValue === '' || optionValue === undefined || optionValue === null) return false;
  if (Array.isArray(value)) {
    return value.includes(optionValue);
  }
  return value === optionValue;
}
/**
 * 过滤搜索选项
 * @param options 选项
 * @param filterValue 过滤字符串
 * @param fieldNames 字段映射
 * @param searchDiscriminateCase 是否区分大小写
 * @param isChildren 是否为子节点
 * @param parentLabel 父节点label
 * @returns 
 */
export function filterSearchOptions(
  options: SelectOption[],
  filterValue: string,
  fieldNames?: FieldNamesType,
  searchDiscriminateCase?: boolean,
  isChildren?: boolean,
  parentLabel?: any
) {
  filterValue = filterValue?.trim();
  if (!filterValue) return options;
  const newOptions: SelectOption[] = [];
  if (Array.isArray(options)) {
    options.forEach(option => {
      const labelName = isChildren ? fieldNames?.childrenLabel || DEFAULT_FIELD_NAMES.childrenLabel : fieldNames?.label || DEFAULT_FIELD_NAMES.label;
      const subLabelName = isChildren ? fieldNames?.childrenSubLabel || DEFAULT_FIELD_NAMES.childrenSubLabel : fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel;
      const childrenName = fieldNames?.children || DEFAULT_FIELD_NAMES.children;
      const label = option[labelName];
      const subLabel = option[subLabelName];
      const children = option[childrenName];
      if (Array.isArray(children) && children.length) {
        const childrenOption = filterSearchOptions(children, filterValue, fieldNames, searchDiscriminateCase, true, label);
        newOptions.push(...childrenOption);
        return;
      }
      if (searchDiscriminateCase) {
        if (label?.toString().includes(filterValue) || subLabel?.toString().includes(filterValue)) {
          if (isChildren) {
            const childrenValueName = fieldNames?.childrenValue || DEFAULT_FIELD_NAMES.childrenValue;
            const childrenValue = option[childrenValueName];
            newOptions.push({
              [fieldNames?.label || DEFAULT_FIELD_NAMES.label]: label,
              [fieldNames?.value || DEFAULT_FIELD_NAMES.value]: childrenValue,
              [fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel]: subLabel,
              searchLabel: `${parentLabel}/${label}`,
            });
          } else {
            newOptions.push({
              ...option,
              [fieldNames?.label || DEFAULT_FIELD_NAMES.label]: label,
              [fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel]: subLabel,
            });
          }
        }
      } else {
        if (label?.toLowerCase?.().includes(filterValue.toLowerCase()) || subLabel?.toLowerCase?.().includes(filterValue.toLowerCase())) {
          if (isChildren) {
            const childrenValueName = fieldNames?.childrenValue || DEFAULT_FIELD_NAMES.childrenValue;
            const childrenValue = option[childrenValueName];
            newOptions.push({
              [fieldNames?.label || DEFAULT_FIELD_NAMES.label]: label,
              [fieldNames?.value || DEFAULT_FIELD_NAMES.value]: childrenValue,
              [fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel]: subLabel,
              searchLabel: `${parentLabel}/${label}`,
            });
          } else {
            newOptions.push({
              ...option,
              [fieldNames?.label || DEFAULT_FIELD_NAMES.label]: label,
              [fieldNames?.subLabel || DEFAULT_FIELD_NAMES.subLabel]: subLabel,
            });
          }
        }
      }
    });
  };
  return newOptions;
}
/**
 * 标记搜索字段
 * @param label 标题字符串
 * @param searchValue 搜索字符串
 * @param searchDiscriminateCase 是否区分大小写
 * @returns 
 */
export function markSearchValue(label: ReactNode, searchValue?: string, searchDiscriminateCase?: boolean): ReactNode {
  searchValue = searchValue?.trim();
  if (!searchValue) return label;
  if (!label) return null;
  if (typeof label !== 'string') return label;
  try {
    const regex = new RegExp(searchValue, searchDiscriminateCase ? 'g' : 'ig');
    const splitLabel = label.split(regex);
    if (splitLabel.length === 1) return label;
    const newLabel = label.replaceAll(regex, `<span class='text-danger'>$&</span>`);
    return newLabel;
  } catch (_: any) {
    return label;
  }
}
/**
 * 获取选项对应的label
 * @param flatOptions 拉平后的选项
 * @param value 选项值
 * @param onRenderLabel 格式化标签
 * @returns 
 */
export function getOptionLabel(flatOptions?: SelectOption[], value?: SelectValue, fieldNames?: FieldNamesType, onRenderLabel?: (record: any) => void) {
  if (value === undefined || value === null) return null;
  const valueName = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
  const childrenValueName = fieldNames?.childrenValue || DEFAULT_FIELD_NAMES.childrenValue;
  if (Array.isArray(value)) {
    const filterItems = flatOptions?.filter(item => value.includes(item[valueName]) || value.includes(item[childrenValueName]));
    return filterItems?.map(item => {
      if (onRenderLabel) {
        return onRenderLabel(item);
      }
      if (item) {
        if (item[IS_CHILDREN]) {
          return item[fieldNames?.childrenLabel || DEFAULT_FIELD_NAMES.childrenLabel];
        }
        return item[fieldNames?.label || DEFAULT_FIELD_NAMES.label];
      }
    });
  }
  const findItem = flatOptions?.find(item => item[valueName] === value || item[childrenValueName] === value);
  if (findItem) {
    if (onRenderLabel) {
      return onRenderLabel(findItem);
    }
    if (findItem[IS_CHILDREN]) {
      return findItem[fieldNames?.childrenLabel || DEFAULT_FIELD_NAMES.childrenLabel];
    }
    return findItem[fieldNames?.label || DEFAULT_FIELD_NAMES.label];
  }
  return onRenderLabel ? onRenderLabel({}) : value;
}
/**
 * 获取选项
 * @param flatOptions 拉平后的选项
 * @param value 选项值
 * @returns 
 */
export function getSelectOptions(flatOptions?: SelectOption[], value?: SelectValue, mode?: modeType, fieldNames?: FieldNamesType): SelectOption | SelectOption[] | undefined {
  if (Array.isArray(flatOptions) && value !== undefined) {
    const valueName = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
    const childrenValueName = fieldNames?.childrenValue || DEFAULT_FIELD_NAMES.childrenValue;
    if (Array.isArray(value)) {
      const filterItems: SelectOption[] = [];
      value.forEach(item => {
        const findItem = flatOptions.find(v => v[valueName] === item || v[childrenValueName] === item);
        filterItems.push(findItem || {});
      });
      return filterItems.map(item => {
        const { [IS_CHILDREN]: _, ...restItem } = item;
        return restItem as SelectOption;
      });
    }
    const findItem = flatOptions.find(item => item[valueName] === value || item[childrenValueName] === value);
    if (findItem?.[IS_CHILDREN]) {
      const { [IS_CHILDREN]: aaa, ...restItem } = findItem;
      return restItem;
    }
    return findItem;
  }
  return mode === 'multiple' ? [] : undefined;
}

/**
 * 拉平数组
 * @param arr
 * @param childrenKey
 */
export const flatArray = (
  arr: any[],
  childrenKey: string = 'children',
  isChildren?: boolean,
): any[] => {
  let result: any[] = [];
  arr.forEach(item => {
    const newItem = { ...item };
    if (isChildren) {
      newItem[IS_CHILDREN] = true;
    }
    result.push({ ...newItem });
    const children = newItem[`${childrenKey}`];
    if (Array.isArray(children)) {
      result.push(...flatArray(children, childrenKey, true));
    }
  });
  return result;
};
